import { useApolloClient } from "@apollo/client";
import {
  LoadingSpinner,
  ModalActions,
  ModalContent,
  ModalTitle,
} from "@msys/ui";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Alert, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { ProjectType } from "../../../../clients/graphqlTypes.js";
import { useCategories } from "../../categories/useCategories.js";
import {
  matrixCellKey,
  OfferedServicesMatrix,
} from "../offeredServices/OfferedServicesMatrix.js";
import { useOnboardingFlowModalQuery } from "./OnboardingFlowModal.generated.js";
import type { OnboardingFlowStage } from "./OnboardingFlowModal.js";
import { useSetOrganisationOfferedServices } from "./useSetOrganisationOfferedServices.js";
import type {
  OnboardingFlowWizardAction,
  OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

interface Props {
  state: OnboardingFlowWizardState;
  dispatch: React.Dispatch<OnboardingFlowWizardAction>;
  setStage: (newStage: OnboardingFlowStage | null) => void;
}

export const OnboardingFlowSpecifyMatrixStage = ({
  state,
  dispatch,
  setStage,
}: Props) => {
  const client = useApolloClient();
  const { t } = useTranslate(["Global", "OrganisationProfile"]);
  const query = useOnboardingFlowModalQuery({ client });
  const { categories: allCategories, loading: categoriesLoading } =
    useCategories();
  const { commit, error, loading } = useSetOrganisationOfferedServices(
    state,
    setStage
  );

  const viewer = query.data?.viewer;

  // Belt-and-suspenders against direct-URL access; server also gates this.
  React.useEffect(() => {
    if (!viewer) return;
    if (viewer.organisation.organisationType !== "CRAFTSMAN") {
      setStage("success");
    }
  }, [viewer, setStage]);

  const declaredCategoryIds = React.useMemo(
    () => new Set(state.matrixDeclaredCategories),
    [state.matrixDeclaredCategories]
  );

  const cellSelections = React.useMemo(() => {
    const set = new Set<string>();
    for (const s of state.matrixSelections) {
      set.add(matrixCellKey(s.categoryId, s.projectType));
    }
    return set;
  }, [state.matrixSelections]);

  const childrenByParent = React.useMemo(() => {
    const map = new Map<string, string[]>();
    for (const c of allCategories) {
      if (!c.parentId) continue;
      const arr = map.get(c.parentId);
      if (arr) arr.push(c.id);
      else map.set(c.parentId, [c.id]);
    }
    return map;
  }, [allCategories]);

  const descendantsByVertical = React.useMemo(() => {
    const descendantsOf = (id: string): string[] => {
      const ids: string[] = [];
      const queue = [id];
      while (queue.length > 0) {
        const cur = queue.shift()!;
        ids.push(cur);
        const kids = childrenByParent.get(cur);
        if (kids) queue.push(...kids);
      }
      return ids;
    };
    const map = new Map<string, string[]>();
    for (const vid of state.selectedVerticals) {
      map.set(vid, descendantsOf(vid));
    }
    return map;
  }, [childrenByParent, state.selectedVerticals]);

  // Ref-guarded so revisits to this stage preserve the user's edits.
  const hasPrefilledRef = React.useRef(false);
  React.useEffect(() => {
    if (hasPrefilledRef.current) return;
    if (
      state.matrixDeclaredCategories.length > 0 ||
      state.matrixSelections.length > 0
    ) {
      hasPrefilledRef.current = true;
      return;
    }
    if (state.selectedVerticals.length === 0 || allCategories.length === 0) {
      return;
    }
    hasPrefilledRef.current = true;
    const allIds = new Set<string>();
    for (const verticalId of state.selectedVerticals) {
      for (const id of descendantsByVertical.get(verticalId) ?? []) {
        allIds.add(id);
      }
    }
    const categoryIds = Array.from(allIds);
    dispatch({ type: "setMatrixDeclaredCategories", categoryIds });
    for (const projectType of state.selectedProjectTypes) {
      dispatch({
        type: "setMatrixCellsForCategoriesAndType",
        categoryIds,
        projectType,
        checked: true,
      });
    }
  }, [
    state.selectedVerticals,
    state.selectedProjectTypes,
    state.matrixDeclaredCategories.length,
    state.matrixSelections.length,
    allCategories.length,
    descendantsByVertical,
    dispatch,
  ]);

  const initiallyExpandedRootIds = React.useMemo(
    () => new Set(state.selectedVerticals),
    [state.selectedVerticals]
  );

  const handleDeclaredChange = React.useCallback(
    (next: readonly string[]) => {
      dispatch({
        type: "setMatrixDeclaredCategories",
        categoryIds: [...next],
      });
    },
    [dispatch]
  );

  const handleToggleCell = React.useCallback(
    (categoryId: string, projectType: ProjectType) => {
      dispatch({ type: "toggleMatrixCell", categoryId, projectType });
    },
    [dispatch]
  );

  if (!viewer || categoriesLoading) {
    return <LoadingSpinner />;
  }
  if (viewer.organisation.organisationType !== "CRAFTSMAN") {
    return null;
  }

  return (
    <>
      <ModalTitle
        title={t("Specify your offered services", {
          ns: "OrganisationProfile",
        })}
      />
      <ModalContent>
        <Stack spacing={2}>
          {error && (
            <Alert severity="error">
              {error.message ||
                t("Something went wrong. Please try again.", {
                  ns: "Global",
                })}
            </Alert>
          )}
          <OfferedServicesMatrix
            categories={allCategories}
            declaredCategoryIds={declaredCategoryIds}
            cellSelections={cellSelections}
            onDeclaredChange={handleDeclaredChange}
            onToggleCell={handleToggleCell}
            initiallyExpandedRootIds={initiallyExpandedRootIds}
          />
        </Stack>
      </ModalContent>
      <ModalActions
        actionButtons={[
          {
            label: t("Back", { ns: "Global" }),
            handleClick: () => setStage("projectTypes"),
            buttonProps: {
              color: "primary",
              variant: "text",
              startIcon: <ChevronLeftIcon />,
              disabled: loading,
            },
          },
          {
            label: t("Skip", { ns: "Global" }),
            handleClick: () => void commit("skip-matrix"),
            buttonProps: {
              color: "primary",
              variant: "text",
              endIcon: <KeyboardDoubleArrowRightIcon />,
              disabled: loading,
            },
          },
          {
            label: t("Continue", { ns: "Global" }),
            handleClick: () => void commit("specify-now-commit"),
            buttonProps: {
              color: "primary",
              variant: "contained",
              endIcon: <ChevronRightIcon />,
              // Bare declarations (categories without services) are valid;
              // matrixSelections is checked as a defence against divergence.
              disabled:
                loading ||
                (state.matrixDeclaredCategories.length === 0 &&
                  state.matrixSelections.length === 0),
            },
          },
        ]}
      />
    </>
  );
};
